// ============================================
// BatchesPage
// Lô & hạn sử dụng (sheet 2 — mục #32)
// ============================================
//
// Nguồn: GET /inventory/batches/ và GET /inventory/batches/expiring/.
//
// NGUYÊN TẮC NỀN: lô là LỚP PHỦ trên tồn kho, không thay thế nó. Tồn kho
// (`InventoryStock.quantity`) vẫn là nguồn sự thật duy nhất về số lượng; sổ lô
// chỉ ghi số đó chia theo lô ra sao. Hệ quả: tổng `remaining_quantity` của các
// lô CÓ THỂ khác tồn kho, và đó là thông tin (sổ lô ghi chưa đủ), KHÔNG phải
// lỗi — nên trang này chỉ ghi chú nhẹ chứ không dựng cảnh báo đỏ.
//
// Trừ kho khi bán chạy FEFO TỰ ĐỘNG ở BE (hạn gần nhất trước). Cửa hàng không
// có nhân viên nên KHÔNG có chỗ chọn lô thủ công ở đây — đừng thêm vào.
//
// Endpoint chưa lên QA nên hook đặt `retry: false`: 404 vào thẳng error state
// thay vì treo loading gần chục giây.

import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { InventoryBatch, Store, UUID } from '@conai/shared-types';
import { useI18n } from '../../i18n';
import {
  PageContainer,
  PageHeader,
  ActionButton,
  SelectDropdown,
  DataTable,
  Pagination,
  type Column,
} from '../../components/shared';
import { useCreateBatch, useExpiringBatches, useInventoryBatches, useStores } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import { extractList } from '../../utils/apiList';
import { ProductPicker, type PickedProduct } from './components';

const PAGE_SIZE = 20;
const EXPIRING_DAY_OPTIONS = [7, 30, 90];

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Một lô đã quá hạn chưa, và quá bao nhiêu ngày.
 *
 * `days_to_expiry` của BE âm khi đã quá hạn. Thiếu field thì tự tính từ
 * `expiry_date` — lô không có hạn KHÔNG bao giờ tính là quá hạn.
 */
function expiryState(batch: InventoryBatch): { expired: boolean; days: number | null } {
  if (batch.days_to_expiry != null) {
    return { expired: batch.days_to_expiry < 0, days: Math.abs(batch.days_to_expiry) };
  }
  if (batch.is_expired === true) return { expired: true, days: null };
  if (!batch.expiry_date) return { expired: false, days: null };
  const expiry = new Date(batch.expiry_date);
  if (Number.isNaN(expiry.getTime())) return { expired: false, days: null };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);
  const diffDays = Math.round((expiry.getTime() - today.getTime()) / 86_400_000);
  return { expired: diffDays < 0, days: Math.abs(diffDays) };
}

/**
 * Một nhóm lô trong khối theo dõi hạn — "đã quá hạn" hoặc "sắp hết hạn".
 *
 * Tiêu đề nhóm mang sắc thái, nên từng dòng KHÔNG lặp lại badge "Expired" nữa:
 * nằm dưới tiêu đề "Đã quá hạn" thì badge đó là thừa. Thay vào đó mỗi dòng cho
 * biết con số thật sự dùng để xếp việc — quá hạn mấy ngày, hoặc còn mấy ngày.
 */
function BatchExpiryGroup({
  tone,
  label,
  batches,
  t,
}: {
  tone: 'expired' | 'upcoming';
  label: string;
  batches: InventoryBatch[];
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const expired = tone === 'expired';
  return (
    <section>
      <div className="mb-1.5 flex items-center gap-1.5">
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${expired ? 'bg-red-500' : 'bg-orange-400'}`}
        />
        <h4
          className={`text-xs font-bold uppercase tracking-wider ${
            expired ? 'text-red-700' : 'text-orange-700'
          }`}
        >
          {label}
        </h4>
      </div>
      <ul className="space-y-2">
        {batches.map((batch) => {
          const { days } = expiryState(batch);
          return (
            <li
              key={batch.id}
              className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                expired ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'
              }`}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  {batch.product_name ?? batch.product_id}
                </p>
                <p className="text-xs text-slate-600">
                  {t('batchCode')}: {batch.batch_code} · {formatDate(batch.expiry_date)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-600">
                  {t('batchRemainingQuantity')}: <strong>{batch.remaining_quantity}</strong>
                </span>
                {/* Không biết còn/quá bao nhiêu ngày thì KHÔNG vẽ badge cho
                    nhóm "sắp hết hạn" — tiêu đề nhóm đã nói rồi, và ngày hết
                    hạn thật nằm ngay dòng trên. Nhóm quá hạn vẫn giữ badge vì
                    đó là cảnh báo, không phải thông tin phụ. */}
                {(days != null || expired) && (
                  <span
                    className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      expired ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    }`}
                  >
                    {days == null
                      ? t('batchExpiredBadge')
                      : expired
                        ? t('batchOverdueDays', { days })
                        : t('batchDaysLeft').replace('{days}', String(days))}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function BatchesPage() {
  const { t } = useI18n();
  const selectedStoreId = useStoreSelectionStore((s) => s.selectedStoreId);

  const [storeFilter, setStoreFilter] = useState<string>(selectedStoreId ?? 'all');
  const [productFilter, setProductFilter] = useState<PickedProduct | null>(null);
  const [includeEmpty, setIncludeEmpty] = useState(false);
  /**
   * Lọc theo hạn — chạy Ở SERVER (bug #245).
   *
   * `/inventory/batches/` giờ nhận `expired=true|false` (BE 18/09/2026), nên
   * bản lọc-ở-client cùng cái hack tắt phân trang đã gỡ hẳn. Ba trạng thái:
   *   'all'     — không gửi param
   *   'expired' — `expired=true`, chỉ lô đã quá hạn
   *   'valid'   — `expired=false`, chỉ lô CÒN HẠN. Lưu ý: BE KHÔNG tính lô
   *               không có ngày hết hạn vào nhóm này, nên chọn 'valid' sẽ
   *               giấu mất hàng không hạn sử dụng — nói rõ dưới ô chọn.
   */
  const [expiryFilter, setExpiryFilter] = useState<'all' | 'expired' | 'valid'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [expiringDays, setExpiringDays] = useState('7');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const { data: storesData } = useStores({ page: 1, page_size: 100, business_status: 'ACTIVE' });
  const stores = useMemo(() => extractList<Store>(storesData), [storesData]);

  const storeParam = storeFilter === 'all' ? undefined : (storeFilter as UUID);

  const { data, isLoading, error } = useInventoryBatches({
    store_id: storeParam,
    product_id: productFilter?.id,
    // Truyền tường minh cả khi false: mặc định của BE cũng là ẩn lô đã hết,
    // nhưng gửi thẳng thì trạng thái toggle luôn khớp với thứ server trả về.
    include_empty: includeEmpty,
    // Lọc hạn ở SERVER (bug #245) — bỏ hẳn `expired` khỏi params khi chọn
    // 'all', vì `expired=false` có nghĩa riêng (chỉ lô còn hạn), không phải
    // "không lọc".
    ...(expiryFilter === 'all' ? {} : { expired: expiryFilter === 'expired' }),
    page: currentPage,
    page_size: PAGE_SIZE,
  });

  const {
    data: expiringData,
    isLoading: isLoadingExpiring,
    error: expiringError,
  } = useExpiringBatches({ store_id: storeParam, days: Number(expiringDays) });

  const rows = useMemo(() => extractList<InventoryBatch>(data), [data]);

  const expiringRows = useMemo(() => extractList<InventoryBatch>(expiringData), [expiringData]);

  /**
   * Tách "ĐÃ quá hạn" khỏi "SẮP hết hạn" (phản hồi QA 19/09/2026).
   *
   * Khối này trước đây tên "Expiring soon" nhưng lại gắn badge "Expired" lên
   * một nửa số dòng — hai chữ chửi nhau ngay trong cùng một khung: "soon" nói
   * tương lai, "Expired" nói quá khứ. Người đọc dừng lại để hiểu, rồi ngờ là
   * hệ thống tính sai.
   *
   * Dữ liệu vẫn y nguyên — endpoint LUÔN trả cả lô quá hạn và đó là chủ ý,
   * hàng quá hạn còn nằm trên kệ mới là thứ nguy hiểm nhất. Chỉ đổi cách bày:
   * hai nhóm có tiêu đề riêng, nhóm quá hạn lên trước vì gấp hơn.
   */
  const expiredBatches = expiringRows.filter((b) => expiryState(b).expired);
  const upcomingBatches = expiringRows.filter((b) => !expiryState(b).expired);

  // Phân trang server hoạt động lại bình thường: lọc hạn đã chuyển sang server
  // nên `count` mô tả đúng thứ đang hiển thị (bug #245).
  const totalItems =
    (data as { count?: number; meta?: { count?: number } } | undefined)?.count ??
    (data as { meta?: { count?: number } } | undefined)?.meta?.count ??
    rows.length;
  const totalPages = Math.max(Math.ceil(totalItems / PAGE_SIZE), 1);

  /**
   * Bộ lọc nào đang GIẤU bớt dòng.
   *
   * Chỉ tính loại lọc làm danh sách hẹp lại. `includeEmpty = true` thì ngược
   * lại — nó NỚI ra — nên không nằm ở đây; nhưng `includeEmpty = false` (mặc
   * định) lại có giấu lô đã hết hàng, nên vẫn nhắc ở dòng gợi ý bên dưới.
   */
  const hidingFilters: string[] = [];
  if (storeFilter !== 'all') hidingFilters.push(t('storeColumn'));
  if (productFilter) hidingFilters.push(t('product'));
  if (expiryFilter !== 'all') hidingFilters.push(t('batchExpiryFilter'));
  const hasHidingFilters = hidingFilters.length > 0;

  const clearFilters = () => {
    setStoreFilter('all');
    setProductFilter(null);
    setExpiryFilter('all');
    setCurrentPage(1);
  };

  const storeOptions = useMemo(
    () => [
      { value: 'all', label: t('allLocations') },
      ...stores.map((store) => ({ value: store.id, label: `${store.code} - ${store.name}` })),
    ],
    [stores, t]
  );

  const columns: Column<InventoryBatch>[] = [
    {
      key: 'product',
      header: t('product'),
      render: (row) => (
        <div>
          <p className="text-sm font-semibold text-slate-900">{row.product_name ?? '—'}</p>
          {/* `product_sku` (BE thêm 18/09/2026) thay cho UUID: mã này tra được
              bên màn Products, còn id nội bộ thì không. */}
          <p className="text-xs text-slate-500">
            {row.product_sku ?? <span className="text-slate-400">ID: {row.product_id}</span>}
          </p>
        </div>
      ),
    },
    // Cột cửa hàng CHỈ hiện khi đang xem nhiều cửa hàng — khoanh về một cửa
    // hàng rồi thì cả cột lặp lại một giá trị, chỉ tốn bề ngang (#244).
    ...(storeFilter === 'all'
      ? [
          {
            key: 'store',
            header: t('storeColumn'),
            width: '160px',
            render: (row: InventoryBatch) => (
              <p className="text-sm text-slate-700">{row.store_name ?? '—'}</p>
            ),
          } as Column<InventoryBatch>,
        ]
      : []),
    {
      key: 'batch_code',
      header: t('batchCode'),
      width: '160px',
      render: (row) => (
        <p className="text-sm font-medium text-slate-700">{row.batch_code}</p>
      ),
    },
    {
      key: 'expiry_date',
      header: t('batchExpiryDate'),
      width: '170px',
      render: (row) => (
        <div>
          <p className="text-sm text-slate-700">
            {row.expiry_date ? formatDate(row.expiry_date) : t('batchNoExpiry')}
          </p>
          {row.days_to_expiry != null && (
            <p className={`text-[11px] font-semibold ${row.is_expired ? 'text-red-600' : 'text-slate-500'}`}>
              {row.is_expired
                ? t('batchExpiredBadge')
                : t('batchDaysLeft').replace('{days}', String(row.days_to_expiry))}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'initial_quantity',
      header: t('batchInitialQuantity'),
      width: '120px',
      render: (row) => <p className="text-sm text-slate-700">{row.initial_quantity}</p>,
    },
    {
      key: 'remaining_quantity',
      header: t('batchRemainingQuantity'),
      width: '130px',
      render: (row) => (
        <p
          className={`text-sm font-bold ${
            row.remaining_quantity > 0 ? 'text-slate-900' : 'text-slate-400'
          }`}
        >
          {row.remaining_quantity}
        </p>
      ),
    },
    {
      key: 'received_at',
      header: t('batchReceivedAt'),
      width: '130px',
      render: (row) => (
        <p className="text-xs font-medium text-slate-600">{formatDate(row.received_at)}</p>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t('batchesTitle')}
        subtitle={t('batchesSubtitle')}
        actions={
          <ActionButton variant="primary" onClick={() => setIsFormOpen(true)}>
            {t('batchAdd')}
          </ActionButton>
        }
      />

      {/* Bug #244 — chọn "All Locations" thì khối này hiện đúng một câu
          "Unable to load batches", còn chọn một cửa hàng cụ thể thì bình thường.
          Thủ phạm là `error instanceof Error`: lỗi API ở đây là `ApiClientError`
          — một object thường, KHÔNG kế thừa `Error` — nên điều kiện luôn sai và
          message thật của server bị thay bằng câu chung chung. Cùng một cái bẫy
          đã được ghi lại ở `InventoryLossStatsPage`.
          Đọc `.message` trực tiếp; `Error` chỉ còn là nhánh dự phòng. */}
      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('batchesLoadError')}</p>
          <p className="mt-1 text-red-600">
            {(error as { message?: string })?.message ||
              (error instanceof Error ? error.message : '')}
          </p>
          {/* Gợi ý "chọn một cửa hàng cụ thể" đã GỠ (bug #244): BE cho
              `store_id` optional từ 18/09/2026, bỏ trống = mọi cửa hàng trong
              scope của user, giống `/inventory/`. "All Locations" chạy được,
              không còn là ca lỗi cần hướng dẫn vòng. */}
        </div>
      )}

      {/* Bộ lọc dùng CHUNG cho cả hai khối: cửa hàng đổi thì khối cận hạn bên
          dưới cũng đổi theo, tránh cảnh hai khối nói về hai cửa hàng khác nhau. */}
      <div className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
            {t('storeColumn')}
          </label>
          <SelectDropdown
            options={storeOptions}
            value={storeFilter}
            onChange={(val) => {
              setStoreFilter(val);
              setCurrentPage(1);
            }}
            className="w-full"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
            {t('product')}
          </label>
          <ProductPicker
            selected={productFilter}
            onSelect={(product) => {
              setProductFilter(product);
              setCurrentPage(1);
            }}
            storeId={storeParam}
            placeholder={t('batchFilterProductHint')}
          />
        </div>

        {/* Bug #245 — ô tích "hết HÀNG" và ô chọn "hết HẠN" là hai chuyện khác
            nhau.
            Ô cũ tên "Show depleted batches" lọc theo SỐ LƯỢNG CÒN LẠI (= 0),
            nhưng người dùng đọc "đã hết" ra thành "đã hết HẠN", tích vào rồi
            chờ thấy danh sách lô quá hạn — và tất nhiên không thấy, nên nó
            trông như một ô tích hỏng. Tách bạch, mỗi ô có chú thích riêng. */}
        <div className="flex flex-col justify-end gap-2">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeEmpty}
              onChange={(e) => {
                setIncludeEmpty(e.target.checked);
                setCurrentPage(1);
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600"
            />
            <span>
              {t('batchIncludeEmpty')}
              <span className="block text-xs font-normal text-slate-400">
                {t('batchIncludeEmptyHint')}
              </span>
            </span>
          </label>

          {/* Ba trạng thái, không phải ô tích hai trạng thái: "còn hạn" là một
              lựa chọn riêng chứ không phải phủ định của "quá hạn" — BE loại lô
              KHÔNG có ngày hết hạn ra khỏi cả hai nhóm. Ô tích không diễn đạt
              nổi ba khả năng đó. */}
          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('batchExpiryFilter')}
            </label>
            <SelectDropdown
              options={[
                { value: 'all', label: t('batchExpiryFilterAll') },
                { value: 'expired', label: t('batchExpiryFilterExpired') },
                { value: 'valid', label: t('batchExpiryFilterValid') },
              ]}
              value={expiryFilter}
              onChange={(val) => {
                setExpiryFilter(val as 'all' | 'expired' | 'valid');
                setCurrentPage(1);
              }}
              className="w-full"
            />
            {/* Cảnh báo đúng chỗ: chọn "còn hạn" là giấu mất hàng không hạn sử
                dụng, mà đó thường là phần lớn hàng phi thực phẩm. */}
            {expiryFilter === 'valid' && (
              <p className="mt-1 text-xs text-amber-600">{t('batchExpiryFilterValidHint')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Bảng lô */}
      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          isLoading={isLoading}
          showFilter={false}
          showHeaderMenu={false}
          /**
           * "No batches recorded yet" là NÓI DỐI khi đang có bộ lọc bật: sổ lô
           * có dữ liệu, chỉ là bộ lọc giấu hết đi.
           *
           * Đây đúng ca đã gặp: hai lô duy nhất trong hệ thống đều quá hạn, bộ
           * lọc để "Still valid only" nên bảng rỗng — trong khi khối "Expiring
           * soon" bên dưới vẫn liệt kê chúng. Hai khối cùng đúng, nhưng câu chữ
           * ở đây khiến nó đọc ra như mâu thuẫn.
           */
          emptyMessage={hasHidingFilters ? t('batchesEmptyFiltered') : t('batchesEmpty')}
          className="border-0 shadow-none"
        />

        {/* Đường thoát + nói rõ bộ lọc NÀO đang bật. Không có nó thì người dùng
            phải tự rà bốn ô lọc để đoán vì sao bảng trống. */}
        {!isLoading && rows.length === 0 && (hasHidingFilters || !includeEmpty) && (
          <div className="border-t border-slate-100 px-6 py-4 text-center">
            {hasHidingFilters && (
              <p className="text-xs text-slate-500">
                {t('batchesEmptyFilterHint', { filters: hidingFilters.join(', ') })}
              </p>
            )}
            {!includeEmpty && (
              <p className="mt-1 text-xs text-slate-400">{t('batchesEmptyDepletedHint')}</p>
            )}
            {hasHidingFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                {t('clearFilters')}
              </button>
            )}
          </div>
        )}
        {!isLoading && totalItems > 0 && (
          <div className="border-t border-slate-100">
            <Pagination
              variant="numbered"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
              itemLabel="batches"
            />
          </div>
        )}
      </div>

      {/* Ghi chú NHẸ, cố ý không phải cảnh báo: chênh lệch giữa tổng lô và tồn
          kho là chuyện bình thường của sổ lô best-effort. */}
      <p className="mt-2 text-xs text-slate-500">{t('batchStockMismatchNote')}</p>

      {/* Khối cận hạn */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {/* Tên khối phải bao được CẢ HAI nhóm bên dưới. "Expiring soon"
                chỉ mô tả một nửa nội dung, nửa còn lại là hàng đã quá hạn. */}
            <h3 className="text-base font-bold text-slate-900">{t('batchExpiryWatchTitle')}</h3>
            <p className="mt-0.5 text-xs text-slate-500">{t('batchExpiryWatchSubtitle')}</p>
            {/* Khối này chạy query RIÊNG (`/batches/expiring/`), chỉ dùng chung
                bộ lọc cửa hàng — không theo ô lọc hạn phía trên, và LUÔN gồm lô
                đã quá hạn. Nói ra, vì cảnh "bảng trên rỗng mà dưới có hàng" đọc
                ra y như lỗi. */}
            <p className="mt-0.5 text-xs text-slate-400">{t('batchExpiringIndependentNote')}</p>
          </div>
          <SelectDropdown
            options={EXPIRING_DAY_OPTIONS.map((d) => ({
              value: String(d),
              label: t('batchExpiringDaysOption').replace('{days}', String(d)),
            }))}
            value={expiringDays}
            onChange={setExpiringDays}
            className="w-full sm:w-[200px]"
          />
        </div>

        {expiringError && (
          <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {expiringError instanceof Error ? expiringError.message : t('batchExpiringLoadError')}
          </div>
        )}

        {isLoadingExpiring && (
          <div className="mt-3 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        )}

        {!isLoadingExpiring && !expiringError && expiringRows.length === 0 && (
          <p className="mt-4 text-sm text-slate-500">{t('batchExpiringEmpty')}</p>
        )}

        {!isLoadingExpiring && expiringRows.length > 0 && (
          <div className="mt-3 space-y-4">
            {/* SẮP HẾT HẠN đứng trước, ĐÃ QUÁ HẠN xuống cuối.
                Khối này để xếp việc sắp tới: hàng còn hạn mà sắp hết mới là thứ
                kịp xoay (đẩy giảm giá, chuyển cửa hàng). Hàng đã quá hạn thì
                nước đã đổ, nó nằm đây để đối chiếu và thanh lý chứ không còn
                quyết định gì — nên ở cuối, vẫn đỏ để không ai bỏ sót. */}
            {upcomingBatches.length > 0 && (
              <BatchExpiryGroup
                tone="upcoming"
                label={t('batchGroupUpcoming', {
                  days: expiringDays,
                  count: upcomingBatches.length,
                })}
                batches={upcomingBatches}
                t={t}
              />
            )}
            {expiredBatches.length > 0 && (
              <BatchExpiryGroup
                tone="expired"
                label={t('batchGroupExpired', { count: expiredBatches.length })}
                batches={expiredBatches}
                t={t}
              />
            )}
          </div>
        )}
      </div>

      {isFormOpen && (
        <BatchFormModal
          stores={stores}
          defaultStoreId={storeParam ?? selectedStoreId ?? undefined}
          onClose={() => setIsFormOpen(false)}
        />
      )}
    </PageContainer>
  );
}

// ============================================
// Modal thêm lô
// ============================================

interface BatchFormModalProps {
  stores: Store[];
  defaultStoreId?: string;
  onClose: () => void;
}

function BatchFormModal({ stores, defaultStoreId, onClose }: BatchFormModalProps) {
  const { t } = useI18n();
  const { mutate: createBatch, isPending } = useCreateBatch();

  const [storeId, setStoreId] = useState<string>(defaultStoreId ?? '');
  const [product, setProduct] = useState<PickedProduct | null>(null);
  const [batchCode, setBatchCode] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [quantity, setQuantity] = useState('');
  /**
   * `null` = CHƯA CHỌN, và form chặn gửi cho tới khi chọn.
   *
   * Cố ý không mặc định `false` như BE: chọn sai giá trị này là cộng kho hai
   * lần (hàng đã vào bằng phiếu nhập mà còn tick "cộng kho") hoặc không lần nào
   * (ghi lô suông cho hàng chưa từng vào kho). Người dùng phải trả lời câu hỏi
   * đó một cách có ý thức.
   */
  const [applyToStock, setApplyToStock] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const storeOptions = useMemo(
    () => stores.map((store) => ({ value: store.id, label: `${store.code} - ${store.name}` })),
    [stores]
  );

  const handleSubmit = () => {
    setError(null);

    if (!storeId) {
      setError(t('batchErrorStore'));
      return;
    }
    if (!product) {
      setError(t('batchErrorProduct'));
      return;
    }
    if (!batchCode.trim()) {
      setError(t('batchErrorCode'));
      return;
    }
    const parsedQuantity = Number.parseInt(quantity, 10);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setError(t('batchErrorQuantity'));
      return;
    }
    if (applyToStock === null) {
      setError(t('batchErrorApplyToStock'));
      return;
    }

    createBatch(
      {
        product_id: product.id,
        store_id: storeId,
        batch_code: batchCode.trim(),
        // Ô trống = hàng KHÔNG có hạn sử dụng, gửi `null` chứ không gửi chuỗi rỗng.
        expiry_date: expiryDate ? expiryDate : null,
        quantity: parsedQuantity,
        apply_to_stock: applyToStock,
      },
      {
        onSuccess: () => {
          toast.success(t('batchCreateSuccess'));
          onClose();
        },
        onError: (err: unknown) => {
          // Trùng (product, store, batch_code) trả 400 — nói thẳng vì đó là lỗi
          // hay gặp nhất khi nhập lại cùng một chuyến hàng.
          setError(err instanceof Error ? err.message : t('batchErrorGeneric'));
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-900">{t('batchAddTitle')}</h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('storeColumn')} <span className="text-red-500">*</span>
            </label>
            <SelectDropdown
              options={storeOptions}
              value={storeId || null}
              onChange={setStoreId}
              placeholder={t('batchSelectStore')}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('product')} <span className="text-red-500">*</span>
            </label>
            <ProductPicker
              selected={product}
              onSelect={setProduct}
              storeId={storeId || undefined}
              placeholder={t('batchFilterProductHint')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('batchCode')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={batchCode}
                onChange={(e) => setBatchCode(e.target.value)}
                placeholder={t('batchCodeHint')}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('quantity')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('batchExpiryDate')}
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
            />
            <p className="mt-1.5 text-xs text-slate-500">{t('batchExpiryDateHint')}</p>
          </div>

          {/* Câu hỏi apply_to_stock — hai lựa chọn tường minh, không có mặc định. */}
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('batchApplyToStockLabel')} <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setApplyToStock(false)}
                className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  applyToStock === false
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className="block text-sm font-semibold text-slate-800">
                  {t('batchApplyToStockOffTitle')}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  {t('batchApplyToStockOffDesc')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setApplyToStock(true)}
                className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  applyToStock === true
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className="block text-sm font-semibold text-slate-800">
                  {t('batchApplyToStockOnTitle')}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  {t('batchApplyToStockOnDesc')}
                </span>
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-500">{t('batchApplyToStockWarning')}</p>
          </div>

          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending}
            className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {isPending ? t('saving') : t('batchSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
